import React, { useState } from "react";

import * as Yup from "yup";
import { useHistory } from "react-router-dom";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import { Formik, Form, Field } from "formik";

import {
	Button,
	TextField,
	Grid,
	InputAdornment,
	IconButton,
	Link,
	FormControl,
	InputLabel,
	MenuItem,
	Select
} from "@material-ui/core";

import { Visibility, VisibilityOff } from "@material-ui/icons";

import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import phoneCountries, {
	defaultPhoneCountryCode,
	formatPhoneCountry
} from "../../utils/phoneCountries";
import AuthLayout from "../../components/AuthLayout";

const useStyles = makeStyles(theme => ({
	form: {
		width: "100%",
		marginTop: theme.spacing(1)
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
		minHeight: 48,
		fontWeight: 700
	}
}));

const UserSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto")
		.max(50, "Muy largo")
		.required("Requerido"),
	password: Yup.string().min(5, "Muy corto").max(50, "Muy largo"),
	email: Yup.string().email("Correo invalido").required("Requerido"),
	contactPhoneCountryCode: Yup.string().required("Requerido"),
	contactPhoneNumber: Yup.string()
		.matches(/^\d{6,15}$/, "Ingresa solo numeros")
		.required("Requerido")
});

const SignUp = () => {
	const classes = useStyles();
	const history = useHistory();

	const initialState = {
		name: "",
		email: "",
		password: "",
		contactPhoneCountryCode: defaultPhoneCountryCode,
		contactPhoneNumber: ""
	};
	const [showPassword, setShowPassword] = useState(false);
	const [user] = useState(initialState);

	const handleSignUp = async values => {
		try {
			await api.post("/auth/signup", {
				...values,
				contactPhoneNumber: String(values.contactPhoneNumber || "").replace(/\D/g, ""),
				timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || ""
			});
			toast.success(i18n.t("signup.toasts.success"));
			history.push("/login");
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<AuthLayout title={i18n.t("signup.title")}>
				<Formik
					initialValues={user}
					enableReinitialize={true}
					validationSchema={UserSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSignUp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ touched, errors, isSubmitting, values, setFieldValue }) => (
						<Form className={classes.form}>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<Field
										as={TextField}
										autoComplete="name"
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										fullWidth
										id="name"
										label={i18n.t("signup.form.name")}
										autoFocus
										margin="normal"
									/>
								</Grid>

								<Grid item xs={12}>
									<Field
										as={TextField}
										variant="outlined"
										fullWidth
										id="email"
										label={i18n.t("signup.form.email")}
										name="email"
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										autoComplete="email"
										margin="normal"
									/>
								</Grid>

								<Grid item xs={12} sm={5}>
									<FormControl
										variant="outlined"
										fullWidth
										error={
											touched.contactPhoneCountryCode &&
											Boolean(errors.contactPhoneCountryCode)
										}
										margin="normal"
									>
										<InputLabel id="contact-phone-country-label">
											Pais
										</InputLabel>
										<Select
											labelId="contact-phone-country-label"
											id="contactPhoneCountryCode"
											name="contactPhoneCountryCode"
											value={values.contactPhoneCountryCode}
											onChange={event =>
												setFieldValue(
													"contactPhoneCountryCode",
													event.target.value
												)
											}
											label="Pais"
										>
											{phoneCountries.map(country => (
												<MenuItem
													key={`${country.code}-${country.dialCode}`}
													value={country.dialCode}
												>
													{formatPhoneCountry(country)}
												</MenuItem>
											))}
										</Select>
									</FormControl>
								</Grid>

								<Grid item xs={12} sm={7}>
									<Field
										as={TextField}
										variant="outlined"
										fullWidth
										id="contactPhoneNumber"
										label="Telefono de contacto"
										name="contactPhoneNumber"
										error={
											touched.contactPhoneNumber &&
											Boolean(errors.contactPhoneNumber)
										}
										helperText={
											touched.contactPhoneNumber &&
											errors.contactPhoneNumber
										}
										inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
										margin="normal"
										onChange={event =>
											setFieldValue(
												"contactPhoneNumber",
												event.target.value.replace(/\D/g, "")
											)
										}
									/>
								</Grid>

								<Grid item xs={12}>
									<Field
										as={TextField}
										variant="outlined"
										fullWidth
										name="password"
										id="password"
										autoComplete="current-password"
										error={touched.password && Boolean(errors.password)}
										helperText={touched.password && errors.password}
										label={i18n.t("signup.form.password")}
										type={showPassword ? "text" : "password"}
										margin="normal"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword(e => !e)}
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
								</Grid>
							</Grid>
							<Button
								type="submit"
								fullWidth
								variant="contained"
								color="primary"
								className={classes.submit}
								disabled={isSubmitting}
							>
								{i18n.t("signup.buttons.submit")}
							</Button>
							<Grid container justifyContent="flex-end">
								<Grid item>
									<Link
										href="#"
										variant="body2"
										component={RouterLink}
										to="/login"
									>
										{i18n.t("signup.buttons.login")}
									</Link>
								</Grid>
							</Grid>
						</Form>
					)}
				</Formik>
		</AuthLayout>
	);
};

export default SignUp;
